import { AddPanelImpressionEvent, AnalyticsService } from 'base/analytics';
import { keyOfObject } from 'base/helpers';
import { LibraryCache } from 'base/library_cache';
import { LibraryService, PublicComponent } from 'base/library_service';
import { Button } from 'base/ui/button';
import { IconArrowForward, IconChevronRight, IconStars } from 'base/ui/icons';
import { InfiniteScroll, Loader, RenderItems } from 'base/ui/infinite_scroll';
import { Inline, Rows, SpacerGrow } from 'base/ui/layout';
import { DividerLine, PanelHeader, PanelScrollable } from 'base/ui/panel';
import { Scrollable } from 'base/ui/scrollable';
import { TextSmallLight } from 'base/ui/typography';
import { useDebounce, useDebouncedValue } from 'base/useDebounce';
import { FeatureFlagsStore } from 'blocks/flag_overrides';
import { observer } from 'mobx-react-lite';
import React, { useEffect } from 'react';
import { Assistant } from '../ai/assistant';
import { ProjectEditingActions } from '../editing_actions/project';
import { anElementInstance, anInlineSection } from '../model';
import { selectionLocale, SelectionStore } from '../selection/store';
import { ProjectSession } from '../session/session';
import { AddRoute, SidebarNavigation } from '../sidebar/routes';
import { AddBySearchDevTool } from './add_by_search_dev_tool';
import { AiGenerateHeader } from './ai_generate_header';
import blankSectionImage from './category_images/blank.file.svg';
import {
  ComponentResultProps,
  ErrorState,
  LoadingResult,
  LoadingResults,
  PageTemplateResultProps,
  SearchNoResults,
  useResultComponents,
} from './component_results';
import {
  PageTemplateCategory,
  PAGE_TEMPLATES,
  SectionCategory,
  sectionNameToButtonLabel,
  SECTION_CATEGORIES,
} from './content';
import { fuzzyTextSearch } from './fuzzy_text_search';
import { getSlotAddPanelOptions } from './slots';
import { SingleSuggestionStore } from './suggestions';
import {
  AddElementButton,
  AddSectionButton,
  CardButtonContent,
  clearSearch,
  GlobalSectionsHeader,
  GotoPageCategoriesRow,
  Search,
} from './ui';
import styles from './wireframe.css';

export const WireframeAddPanel = observer(function _WireframeAddPanel({
  editing,
  selection,
  session,
  library,
  assistant,
  navigation,
  analytics,
  route,
  flags,
}: {
  editing: ProjectEditingActions;
  selection: SelectionStore;
  session: ProjectSession;
  library: LibraryCache;
  assistant: Assistant;
  analytics: AnalyticsService;
  navigation: SidebarNavigation;
  route: AddRoute;
  flags: FeatureFlagsStore;
}) {
  const [suggestionStore] = React.useState(() => new SingleSuggestionStore());
  const { ComponentResult, PageTemplateResult } = useResultComponents(
    selection,
    session,
    library,
    assistant,
    analytics,
    suggestionStore,
  );
  useImpressionEvent(route, analytics);

  const selectedElement = selection.get().element;

  const trackDebounced = useDebounce(
    // This needs to be bound otherwise the wrong `this` is used … I love JS "classes"
    analytics.track.bind(analytics),
    1000,
  );

  const tab =
    route.path === 'add/search'
      ? route.filter.type
      : route.path === 'add/page-categories'
      ? 'pages'
      : 'sections';
  const search = route.path === 'add/search' ? route.search : '';

  useEffect(() => {
    if (search === '') {
      return;
    }

    trackDebounced({
      event: 'project:component_search_query_updated',
      query: search,
      component_type: tab,
    });
  }, [search, tab, trackDebounced]);

  const query = useDebouncedValue(route.search, 300);

  const panelTitle = route.filter?.category
    ? route.filter.category.name
    : route.path === 'add/page-categories'
    ? 'Add a page'
    : 'Add';

  return (
    <Rows spacing={0} fixedHeight>
      <Rows spacing={12}>
        <PanelHeader title={panelTitle} navigation={navigation} />
        {selectedElement === undefined && (
          <Search navigation={navigation} route={route} />
        )}
        <DividerLine expandToEdges={true} />
      </Rows>
      {/* We want to restore the position in the category lists when people come back, so we don't unmount them */}
      <PanelScrollable hide={route.path !== 'add'}>
        <Rows spacing={8}>
          {session.flags.get('enableDevTools') && (
            <AddBySearchDevTool selection={selection} library={library} />
          )}
          <AiGenerateHeader
            editing={editing}
            session={session}
            selection={selection}
            navigation={navigation}
          />
          <AiSuggestionsRow
            selection={selection}
            navigation={navigation}
            ComponentResult={ComponentResult}
            assistant={assistant}
            library={library}
          />
          {!route.search && selection.get().section == null && (
            <GotoPageCategoriesRow navigation={navigation} />
          )}
          <GlobalSectionsHeader
            selection={selection}
            analytics={analytics}
            suggestionStore={suggestionStore}
            library={library}
          />
          <AddSectionButton
            section={anInlineSection()}
            selection={selection}
            suggestionStore={suggestionStore}
            analytics={analytics}
            library={library}
            size="medium"
          >
            <CardButtonContent
              label="Blank Section"
              image={blankSectionImage}
            />
          </AddSectionButton>
          <SectionCategoriesList navigation={navigation} />
        </Rows>
      </PanelScrollable>
      <PanelScrollable hide={route.path !== 'add/page-categories'}>
        <Rows spacing={8}>
          <PageTemplateCategoriesList navigation={navigation} />
        </Rows>
      </PanelScrollable>

      {/* People can't come back to the result lists, so we unmount them */}
      {route.path === 'add/search' && route.filter.type === 'sections' && (
        <PanelScrollable>
          <Rows spacing={8}>
            <GlobalSectionsHeader
              search={`${route.filter.category?.name || ''} ${route.search}`}
              selection={selection}
              analytics={analytics}
              suggestionStore={suggestionStore}
              library={library}
            />
            <ComponentsList
              search={query ?? ''}
              category={route.filter.category}
              service={library}
              clearSearch={() => clearSearch(navigation, route)}
              goBackHome={() => navigation.goto({ path: 'add' })}
              ComponentResult={ComponentResult}
            />
          </Rows>
        </PanelScrollable>
      )}
      {route.path === 'add/search' && route.filter.type === 'pages' && (
        <PanelScrollable>
          <Rows spacing={8}>
            <PageTemplatesList
              search={route.search || ''}
              category={route.filter.category}
              clearSearch={() => clearSearch(navigation, route)}
              PageTemplateResult={PageTemplateResult}
            />
          </Rows>
        </PanelScrollable>
      )}
    </Rows>
  );
});

function useImpressionEvent(route: AddRoute, analytics: AnalyticsService) {
  const type =
    route.filter?.type ||
    (route.path === 'add/page-categories' ? 'pages' : 'sections');
  const search = route.search;
  const categoryName = route.filter?.category?.name;
  const event = React.useMemo(
    (): AddPanelImpressionEvent | undefined =>
      categoryName || search
        ? {
            event: 'blocks/add-panel-impression',
            type,
            search,
            categoryName,
          }
        : undefined,
    [type, search, categoryName],
  );

  React.useEffect(() => {
    if (!event) return;
    // Only count an impression after 3 seconds.
    // Otherwise you'd get "s" "se" "sea" "searc" "search" as separate events
    const id = setTimeout(() => analytics.track(event), 3000);
    return () => clearTimeout(id);
  }, [analytics, event]);
}

const AiSuggestionsRow = observer(function _AiSuggestionsRow({
  selection,
  navigation,
  ComponentResult,
  assistant,
  library,
}: {
  selection: SelectionStore;
  navigation: SidebarNavigation;
  ComponentResult: React.ComponentType<ComponentResultProps>;
  assistant: Assistant;
  library: LibraryService;
}) {
  const sel = selection.get();
  const [state, setState] = React.useState<
    | {
        type: 'hidden';
      }
    | {
        type: 'pending';
      }
    | {
        type: 'resolved';
        components: PublicComponent[];
        search: string;
      }
    | {
        type: 'rejected';
        error: unknown;
      }
  >({ type: 'pending' });

  React.useEffect(
    () => {
      const section = sel.section
        ? sel.section.type === 'inline'
          ? sel.section.value
          : selection.project.globalSections.get(sel.section.id)
        : null;
      if (
        section == null ||
        section.element.current != null ||
        sel.page == null
      ) {
        setState({ type: 'hidden' });
        return;
      }

      let cancelled = false;
      (async () => {
        setState({ type: 'pending' });
        try {
          const category = await assistant.getBlocksCategoryForSection(
            selectionLocale(sel, selection.project),
            sel.page.state,
            section.state,
          );
          if (cancelled) return;
          if (!category) {
            setState({ type: 'hidden' });
            return;
          }
          const { components } = await library.findPublicComponents({
            filterBlocksCategoryUser: [category],
            filterKind: 'component',
          });
          if (cancelled) return;
          setState(
            components.length === 0
              ? { type: 'hidden' }
              : { type: 'resolved', components, search: category },
          );
        } catch (error) {
          if (cancelled) return;
          setState({ type: 'rejected', error });
        }
      })();
      return () => {
        cancelled = true;
      };
    }, // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sel.section, sel.page, assistant],
  );

  const handleClickViewAll = () => {
    if (state.type !== 'resolved') return;
    navigation.goto({
      path: 'add/search',
      filter: { type: 'sections' },
      search: state.search,
    });
  };

  if (state.type === 'hidden') return null;
  return (
    <div className={styles.aiSuggestionsRow}>
      <Inline yalign="center" spacing={8}>
        <TextSmallLight color="grey">
          <span className={styles.aiInlineIcon}>
            <IconStars />
          </span>
          Suggested
        </TextSmallLight>
        <SpacerGrow />
        <Button
          size="small"
          variant="subtle"
          onClick={handleClickViewAll}
          disabled={state.type !== 'resolved'}
          label="View all"
        />
      </Inline>
      <Scrollable direction="horizontal">
        <div className={styles.aiSuggestionsList}>
          {state.type === 'resolved' &&
            state.components.slice(0, 5).map((c) => (
              <div className={styles.aiSuggestion} key={keyOfObject(c)}>
                <ComponentResult
                  component={c}
                  interaction="add"
                  analyticsContext={{ categoryName: 'ai-suggestions-row' }}
                />
              </div>
            ))}
          {state.type === 'resolved' && state.components.length > 5 && (
            <div className={styles.aiSuggestionsViewAll}>
              <Button
                icon={IconArrowForward}
                tooltip="View all"
                size="large"
                onClick={handleClickViewAll}
              />
            </div>
          )}

          {state.type === 'pending' &&
            Array.from({ length: 3 }).map((_, i) => (
              <div className={styles.aiSuggestion} key={i}>
                <LoadingResult index={i} />
              </div>
            ))}
          {state.type === 'rejected' && (
            <TextSmallLight color="grey">
              Error: {`${state.error}`}
            </TextSmallLight>
          )}
        </div>
      </Scrollable>
    </div>
  );
});

function SectionCategoriesList({
  navigation,
}: {
  navigation: SidebarNavigation;
}) {
  return (
    <>
      {SECTION_CATEGORIES.map((category, i) => (
        <Button
          key={i}
          onClick={() => {
            navigation.goto({
              path: 'add/search',
              search: '',
              filter: { type: 'sections', category },
            });
          }}
          fullWidth
          variant="secondary"
          size="large"
          tooltip={category.description}
          tooltipPlacement="horizontal"
        >
          <CardButtonContent
            label={sectionNameToButtonLabel(category.name)}
            image={category.image}
            icon={IconChevronRight}
          />
        </Button>
      ))}
    </>
  );
}

function PageTemplateCategoriesList({
  navigation,
}: {
  navigation: SidebarNavigation;
}) {
  return (
    <Rows spacing={8}>
      {PAGE_TEMPLATES.map((category, i) => (
        <Button
          key={i}
          onClick={() => {
            navigation.goto({
              path: 'add/search',
              search: '',
              filter: { type: 'pages', category },
            });
          }}
          fullWidth
          variant="secondary"
          size="large"
        >
          <CardButtonContent
            label={category.name}
            image={category.vectorUrl}
            icon={IconChevronRight}
          />
        </Button>
      ))}
    </Rows>
  );
}

function PageTemplatesList({
  search,
  category,
  clearSearch,
  PageTemplateResult,
}: {
  search: string;
  category: PageTemplateCategory | undefined;
  clearSearch: () => void;
  PageTemplateResult: React.ComponentType<PageTemplateResultProps>;
}) {
  const matching = React.useMemo(() => {
    const all = category
      ? category.pages
      : PAGE_TEMPLATES.flatMap((category) => category.pages);
    return fuzzyTextSearch(all, search, (page) => page.name);
  }, [search, category]);

  const analyticsContext = React.useMemo(
    () => ({
      categoryName: category?.name,
      search,
    }),
    [category, search],
  );

  return (
    <Rows spacing={8}>
      {matching.map((page) => (
        <PageTemplateResult
          key={keyOfObject(page)}
          pageTemplate={page}
          analyticsContext={analyticsContext}
        />
      ))}
      {matching.length === 0 && (
        <SearchNoResults clearSearch={clearSearch} subject="page" />
      )}
    </Rows>
  );
}

function ComponentsList({
  search,
  category,
  service,
  clearSearch,
  goBackHome,
  ComponentResult,
}: {
  search: string;
  category?: SectionCategory;
  service: LibraryService;
  clearSearch: () => void;
  goBackHome: () => void;
  ComponentResult: React.ComponentType<ComponentResultProps>;
}) {
  const searchLoader = React.useCallback<Loader<PublicComponent>>(
    async (continuation) => {
      const res = await service.findPublicComponents(
        {
          search: search,
          filterBlocksCategoryUser: category ? [category.name] : [],
          filterKind: 'component',
          excludeAppComponents: true,
        },
        continuation,
      );
      return {
        items: res.components,
        continuation: res.continuation || undefined,
      };
    },
    [search, category, service],
  );
  const searchRender = React.useCallback<RenderItems<PublicComponent>>(
    (items) => {
      if (items.length === 0) {
        return <SearchNoResults clearSearch={clearSearch} subject="section" />;
      }
      return items.map((c) => (
        <ComponentResult
          key={c.componentSlug}
          component={c}
          category={category}
          interaction="add"
          onUsed={goBackHome}
          analyticsContext={{
            search,
            categoryName: category?.name,
          }}
        />
      ));
    },
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  return (
    <InfiniteScroll
      loader={searchLoader}
      render={searchRender}
      LoadingState={LoadingResults}
      ErrorState={ErrorState}
    />
  );
}

const ElementsList = observer(function _ElementsList({
  editing,
  suggestionStore,
  selection,
  library,
  assistant,
}: {
  editing: ProjectEditingActions;
  suggestionStore: SingleSuggestionStore;
  selection: SelectionStore;
  library: LibraryCache;
  assistant: Assistant;
}) {
  const sel = selection.get();
  if (!sel.element || !sel.section) {
    return null;
  }

  const elementEditing = editing.forElementNode(sel);
  const parentSlot = elementEditing.parentSlot;
  if (!parentSlot) return null;

  const elementTypes = library.getCachedElementTypes({
    fetchIfMissing: true,
  })?.value;
  if (!elementTypes) return null;

  const options = getSlotAddPanelOptions({
    slotType: parentSlot.type,
    elementTypes,
  });

  return (
    <>
      <TextSmallLight color="grey">Elements</TextSmallLight>
      {options.map((option, idx) => (
        <AddElementButton
          key={idx}
          element={anElementInstance({
            source: { slug: option.elementId, rootName: undefined },
          })}
          size="medium"
          selection={selection}
          suggestionStore={suggestionStore}
          sidebarNavigation={editing.sidebarNavigation}
          library={library}
          assistant={assistant}
        >
          <CardButtonContent label={option.label} image={option.image} />
        </AddElementButton>
      ))}
    </>
  );
});
